import { createContext, useContext, useState, useEffect } from 'react';

// Available currencies (independent from regions)
const CURRENCIES = {
  aed: {
    id: 'aed',
    code: 'AED',
    symbol: 'د.إ',
    name: 'درهم إماراتي',
    nameEn: 'UAE Dirham',
    position: 'after',
    symbolImageLight: '/currency-aed-black.png', // Black for light mode
    symbolImageDark: '/currency-aed-white.png'   // White for dark mode
  },
  usd: {
    id: 'usd',
    code: 'USD',
    symbol: '$',
    name: 'دولار أمريكي',
    nameEn: 'US Dollar',
    position: 'before',
    symbolImageLight: null,
    symbolImageDark: null
  },
  eur: {
    id: 'eur',
    code: 'EUR',
    symbol: '€',
    name: 'يورو',
    nameEn: 'Euro',
    position: 'before',
    symbolImageLight: null,
    symbolImageDark: null
  },
  cny: {
    id: 'cny',
    code: 'CNY',
    symbol: '¥',
    name: 'يوان صيني',
    nameEn: 'Chinese Yuan',
    position: 'before',
    symbolImageLight: null,
    symbolImageDark: null
  }
};

// Available regions configuration
const REGIONS = {
  uae: {
    id: 'uae',
    name: 'الإمارات العربية المتحدة',
    nameEn: 'United Arab Emirates',
    flag: '🇦🇪',
    flagImage: 'https://customer-assets.emergentagent.com/job_cloudgm-hub-1/artifacts/68n9ppc8_united-arab-emirates%20%281%29.png',
    phone: '+971',
    available: true
  }
};

const RegionContext = createContext();

export function RegionProvider({ children }) {
  const [currentRegion, setCurrentRegion] = useState(() => {
    const saved = localStorage.getItem('cloudgm_region');
    return saved && REGIONS[saved] ? saved : 'uae';
  });

  const [currentCurrency, setCurrentCurrency] = useState(() => {
    const saved = localStorage.getItem('cloudgm_currency');
    return saved && CURRENCIES[saved] ? saved : 'aed';
  });

  useEffect(() => {
    localStorage.setItem('cloudgm_region', currentRegion);
  }, [currentRegion]);

  useEffect(() => {
    localStorage.setItem('cloudgm_currency', currentCurrency);
  }, [currentCurrency]);

  const region = REGIONS[currentRegion];
  const currency = CURRENCIES[currentCurrency];

  // Format price with currency symbol.
  // iter-202 policy: AED amounts are FLOORED to fils (2 decimals) — never rounded.
  // We apply the same flooring rule to any currency for consistency.
  const formatPrice = (amount, showCode = false, _useCurrencyImage = false) => {
    if (amount === null || amount === undefined || amount === '') return '';

    const numAmount = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : amount;
    if (!Number.isFinite(numAmount)) return amount;

    // Floor to fils (2dp) — safety margin against floating-point drift
    const sign = numAmount < 0 ? -1 : 1;
    const floored = (sign * Math.floor(Math.abs(numAmount) * 100 + 1e-6)) / 100;

    const formattedNumber = floored.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (showCode) return `${formattedNumber} ${currency.code}`;
    if (currency.position === 'before') return `${currency.symbol}${formattedNumber}`;
    return `${formattedNumber} ${currency.code}`;
  };

  // Get available regions
  const getAvailableRegions = () => {
    return Object.values(REGIONS).filter(r => r.available);
  };

  // Get all regions
  const getAllRegions = () => {
    return Object.values(REGIONS);
  };

  // Get all currencies
  const getAllCurrencies = () => {
    return Object.values(CURRENCIES);
  };

  // Change region
  const changeRegion = (regionId) => {
    if (REGIONS[regionId] && REGIONS[regionId].available) {
      setCurrentRegion(regionId);
      return true;
    }
    return false;
  };

  // Change currency
  const changeCurrency = (currencyId) => {
    if (CURRENCIES[currencyId]) {
      setCurrentCurrency(currencyId);
      return true;
    }
    return false;
  };

  const value = {
    currentRegion,
    currentCurrency,
    region,
    currency,
    formatPrice,
    getAvailableRegions,
    getAllRegions,
    getAllCurrencies,
    changeRegion,
    changeCurrency,
    REGIONS,
    CURRENCIES
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}

export { REGIONS, CURRENCIES };
